/**
 * THE FEEDBACK TRIGGER PICKER — one of them, for every block that has one.
 *
 * Six blocks had their own: `MCQTriggerDropdown`, `DropdownTriggerDropdown`, `KeywordTriggerDropdown`,
 * `FitbTriggerDropdown`, `ScaleTriggerDropdown` and `OrderingTriggerDropdown`. Diffed, two of them
 * were byte-identical apart from the block's name, whitespace and one `font-mono`. The shell — the
 * listbox roles, the keyboard handling, the chip tone, the symbols, the option list — was copied
 * six times.
 *
 * What genuinely differed is small and is props here:
 *
 * - **Where the trigger's evaluation comes from.** MCQ reads it off the SELECTED OPTION, because an
 *   MCQ option's role IS its correctness and a teacher cannot cycle it. Ordering takes it from the
 *   CARD, because "adjacent swap" has no inherent correctness and the teacher decides. Pass
 *   `evaluation` for the second; omit it for the first. This is a real distinction, not drift.
 * - **A slashed trigger.** MCQ's `_absent` means "this correct answer was NOT selected", and the
 *   line crosses the whole control rather than the preview inside it, because a line through only
 *   the answer says the opposite.
 * - **What the trigger shows.** A node, supplied by the caller. Callers pass
 *   `authoredPreviewNode`, which is what makes an equation render and a media item carry its glyph
 *   — so this component needs to know nothing about rich text, media or formulas.
 *
 * Per-option icons appear only when some option actually carries an evaluation. That is what lets
 * Ordering — whose options are structural kinds with no correctness of their own — get a clean
 * list with no leading spacers, without a prop saying so.
 */
import { ChevronDown, Check, Minus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ChipDivider, SlashedLabel, chipButtonClass, chipToneClass, type ChipEvaluation } from './eval-chip';
import { useListboxMenu } from './use-listbox-menu';

export interface TriggerDropdownOption {
  value: string;
  /** The MENU row. A node so a block can set its own typography — Scale and Keyword use mono. */
  label: React.ReactNode;
  /** What the TRIGGER shows once chosen. Falls back to `label`. */
  preview?: React.ReactNode;
  /** Drives this row's icon, and the trigger's tone when no `evaluation` prop is given. */
  evaluation?: ChipEvaluation;
}

function EvalIcon({ evaluation, strokeWidth }: { evaluation: ChipEvaluation | undefined; strokeWidth: number }) {
  if (evaluation === 'correct') return <Check aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={strokeWidth} />;
  if (evaluation === 'incorrect') return <X aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={strokeWidth} />;
  if (evaluation === 'neutral') return <Minus aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={2} />;
  /* A spacer, so rows with and without an evaluation still line up in a list that has both. */
  return <span aria-hidden='true' className='h-3 w-3 shrink-0' />;
}

export function TriggerDropdown({
  value, options, onChange, ariaLabel, evaluation, slashWhen,
}: {
  value: string;
  options: TriggerDropdownOption[];
  onChange: (value: string) => void;
  /** Omitted, the shared "feedback trigger" name is used — which is what every caller wanted. */
  ariaLabel?: string;
  /** Set to take the trigger's tone and symbol from the CARD rather than the selected option. */
  evaluation?: ChipEvaluation;
  slashWhen?: (value: string) => boolean;
}) {
  const { t } = useTranslation();
  const menu = useListboxMenu({ selectedIndex: options.findIndex(o => o.value === value) });
  const open = menu.open;
  const current = options.find(o => o.value === value) ?? options[0];
  const name = ariaLabel ?? t('blocks.branching.feedbackTriggerLabel');
  const tone = evaluation ?? current?.evaluation ?? 'neutral';
  const slashed = !!(current && slashWhen?.(current.value));
  /* Only where the list actually distinguishes them — see the file's own note. */
  const withIcons = options.some(o => o.evaluation !== undefined);

  return (
    <div ref={menu.wrapperRef} className='relative w-full min-w-0' onBlur={menu.onWrapperBlur}>
      <button
        ref={menu.triggerRef}
        type='button'
        onClick={() => menu.setOpen(v => !v)}
        onKeyDown={menu.onTriggerKeyDown}
        /* `combobox`, not a bare button. A button promises "something happens when you press this";
           a combobox promises "you are choosing among options". */
        role='combobox'
        aria-haspopup='listbox'
        aria-controls={open ? menu.listboxId : undefined}
        aria-expanded={open}
        aria-label={name}
        className={`${chipButtonClass(chipToneClass(tone))}${slashed ? ' relative' : ''}`}
      >
        <EvalIcon evaluation={tone} strokeWidth={tone === 'neutral' ? 2 : 3} />
        <ChipDivider />
        <SlashedLabel slashed={slashed} className='min-w-0 flex-1'>
          <span className='min-w-0 flex-1 truncate text-left'>{current?.preview ?? current?.label ?? ''}</span>
        </SlashedLabel>
        <ChevronDown aria-hidden='true'
          className={`h-3 w-3 shrink-0 opacity-50 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul
          id={menu.listboxId}
          role='listbox'
          aria-label={name}
          onKeyDown={menu.onListboxKeyDown}
          className='absolute left-0 top-full z-20 mt-1 max-h-48 w-max min-w-full max-w-[min(22rem,80vw)] overflow-y-auto overscroll-contain rounded-lg border border-ws-border bg-ws-elevated py-1 shadow-lg'
        >
          {options.map((opt, i) => {
            /* A rule where the list crosses from "no correctness" into graded options. */
            const divider = i > 0 && opt.evaluation !== undefined && options[i - 1]?.evaluation === undefined;
            return (
              <li key={opt.value}>
                {divider && <div className='my-1 border-t border-ws-border' role='separator' />}
                <button
                  ref={menu.optionRef(i)}
                  type='button'
                  role='option'
                  aria-selected={opt.value === value}
                  tabIndex={-1}
                  onClick={() => { onChange(opt.value); menu.closeAndFocus(); }}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-ws-hover focus:bg-ws-hover focus:outline-none ${
                    opt.value === value ? 'font-medium text-ws-primary' : 'text-ws-text'}`}
                >
                  {withIcons && <EvalIcon evaluation={opt.evaluation} strokeWidth={2.5} />}
                  {withIcons && <ChipDivider />}
                  {/* min-w-0 so it can shrink, break-words so it wraps instead of spilling — a bare
                      text node could do neither, and a suffixed option overflowed the menu. */}
                  <span className='min-w-0 flex-1 break-words'>{opt.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
